/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import type { YextStoreEntity } from '../interface/yext-types';
import { getStoreTemporaryClosure } from '../utils/getStoreTemporaryClosure';
import { formatReopenDate } from '../constants/formatReopenDate';

const labelMap = {
  closedUntil: 'label_locations_storeStatus_closedUntil',
};

type StoreClosedUntilStore = Pick<YextStoreEntity, 'hours' | 'timezone'>;

/**
 * Returns the "Closed until <date>" text for a store whose Yext `hours.reopenDate`
 * marks it as temporarily closed, or null when there is no active closure — in
 * which case the caller renders its usual open/closed status instead.
 *
 * Shared by every card that shows a store status, so the closure check, the label
 * and the locale-aware date format stay in one place.
 */
export function useStoreClosedUntilText(store: StoreClosedUntilStore): string | null {
  const labels = useLabels(labelMap);
  const locale = useLocale();

  const temporaryClosure = getStoreTemporaryClosure(store.hours?.reopenDate, store.timezone);

  if (!temporaryClosure) {
    return null;
  }

  return `${labels.closedUntil} ${formatReopenDate(temporaryClosure.reopenDate, locale)}`;
}
